import { useState, useMemo, useCallback, useContext } from 'react';
import { Settings } from 'react-feather';
import Select from 'react-select';
import styled from '@emotion/styled';
import { useFloating, useInteractions, useClick, useDismiss } from '@floating-ui/react';
import type { QuickChartData } from '@trinity/chat';
import { ChartThemePicker } from '@trinity/chat-components/components/ChartTheming';
import { BrandThemingContext } from '../../containers/Branding/Context.js';
import { buildSelectStyles, buildSelectTheme } from '../Chat.Filters/Presentation.js';
import { Portal } from '../Portal/Portal.js';
import { useTheme } from '../Theme/useTheme.js';

type ThemePickerProps = {
  selectedThemeId: number | null;
  onThemeSelect: (themeId: number | null) => void;
  onCreateTheme?: ((name: string, colors: string[], categoryId: number) => void) | null;
};

type ChartSettingsProps = {
  chartData: QuickChartData;
  originalChartType?: QuickChartData['type'] | null;
  selectedCellCount?: number;
  selectedRowCount?: number;
  selectedColumnCount?: number;
  onChartTypeChange: (chartType: QuickChartData['type']) => void;
  themePickerProps?: ThemePickerProps;
};

export function ChartSettings({
  chartData,
  originalChartType,
  selectedCellCount = 0,
  selectedRowCount = 0,
  selectedColumnCount = 0,
  onChartTypeChange,
  themePickerProps,
}: ChartSettingsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const theme = useTheme();
  const brandThemingContext = useContext(BrandThemingContext);

  // SETTINGS MENU FLOATING LOGIC
  const { context, refs, floatingStyles } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-end',
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getFloatingProps, getReferenceProps } = useInteractions([click, dismiss]);

  const setFloatingRef = useCallback(
    (node: HTMLElement | null) => {
      refs.setFloating(node);
    },
    [refs]
  );

  const currentChartType = chartData.type;

  // Chart type options based on original chart type (not current selection)
  const chartTypeOptions = useMemo(() => {
    // Define single and multiple chart options
    const singleOptions = [
      { value: 'bar', label: 'Bar', isDisabled: false },
      { value: 'pie', label: 'Pie', isDisabled: false },
      { value: 'stacked-bar', label: 'Singular Stacked Bar', isDisabled: false },
    ];

    const multipleOptions = [
      { value: 'multiple-clustered-bar', label: 'Multiple Clustered Bar', isDisabled: false },
      { value: 'multiple-stacked-bar', label: 'Multiple Stacked Bar', isDisabled: false },
    ];

    const alwaysAvailableOptions = [
      { value: 'data-table', label: 'Data Table', isDisabled: false },
      { value: 'kpi-widget', label: 'Metric Card', isDisabled: false },
      { value: 'progress-bar-widget', label: 'Progress Bars', isDisabled: false },
      { value: 'semi-circle-progress-widget', label: 'Semi-Circle Progress', isDisabled: false },
    ];

    // Determine which options to show based on original/base chart type
    const singleChartTypes = ['bar', 'pie', 'stacked-bar'];
    const multipleChartTypes = ['multiple-clustered-bar', 'multiple-stacked-bar'];

    if (singleChartTypes.includes(originalChartType)) {
      return [
        ...singleOptions,
        ...multipleOptions.map((opt) => ({ ...opt, isDisabled: true })),
        ...alwaysAvailableOptions,
      ];
    } else if (multipleChartTypes.includes(originalChartType)) {
      return [
        ...singleOptions.map((opt) => ({ ...opt, isDisabled: true })),
        ...multipleOptions,
        ...alwaysAvailableOptions,
      ];
    } else {
      // If no originalChartType but we have selected cells, dynamically determine
      // based on selection dimensions:
      // - Single row OR single column → enable single chart types
      // - Multiple rows AND multiple columns → enable multiple chart types
      if (selectedCellCount > 0) {
        const isSingleDimension = selectedRowCount === 1 || selectedColumnCount === 1;

        if (isSingleDimension) {
          // Single dimension: enable single chart types only
          return [
            ...singleOptions,
            ...multipleOptions.map((opt) => ({ ...opt, isDisabled: true })),
            ...alwaysAvailableOptions,
          ];
        } else {
          // Multiple dimensions: enable multiple chart types only (selectedCellCount > 0 implies
          // row/column counts >= 1, so !isSingleDimension means both are > 1)
          return [
            ...singleOptions.map((opt) => ({ ...opt, isDisabled: true })),
            ...multipleOptions,
            ...alwaysAvailableOptions,
          ];
        }
      }

      return [
        ...singleOptions.map((opt) => ({ ...opt, isDisabled: true })),
        ...multipleOptions.map((opt) => ({ ...opt, isDisabled: true })),
        ...alwaysAvailableOptions,
      ];
    }
  }, [originalChartType, selectedCellCount, selectedRowCount, selectedColumnCount]);

  const selectStyles = useMemo(() => {
    const baseStyles = buildSelectStyles(theme);
    return {
      ...baseStyles,
      option: (provided, state) => {
        const baseOption = baseStyles.option?.(provided, state) || provided;
        return {
          ...baseOption,
          ...(state.isDisabled && { color: '#999' }),
          cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        };
      },
    };
  }, [theme]);
  const selectTheme = useMemo(() => buildSelectTheme(theme), [theme]);

  const setReferenceRef = useCallback(
    (node: Element | null) => {
      refs.setReference(node);
    },
    [refs]
  );

  const handleChartTypeChange = (option: { value: string } | null) => {
    if (!option) return;

    const newChartType = option.value as QuickChartData['type'];

    if (newChartType !== currentChartType) {
      onChartTypeChange(newChartType);
    }
  };

  return (
    <TopRow>
      {/* SETTINGS BUTTON */}
      <SettingsButton ref={setReferenceRef} {...getReferenceProps()}>
        <StyledSettingsIcon />
      </SettingsButton>

      {/* SETTINGS MENU */}
      {isOpen && (
        <Portal>
          <Root ref={setFloatingRef} style={floatingStyles} {...getFloatingProps()}>
            <Header>
              <HeaderLabel>Chart Settings</HeaderLabel>
            </Header>
            <Content>
              <LabelContainer>
                <Label>Chart Type</Label>
              </LabelContainer>
              <Select
                value={chartTypeOptions.find((option) => option.value === currentChartType)}
                onChange={handleChartTypeChange}
                options={chartTypeOptions}
                styles={selectStyles}
                theme={selectTheme}
                placeholder="Select chart type..."
              />

              {themePickerProps && (
                <>
                  <Divider />
                  <ChartThemePicker
                    selectedThemeId={themePickerProps.selectedThemeId}
                    onThemeSelect={themePickerProps.onThemeSelect}
                    onCreateTheme={themePickerProps.onCreateTheme}
                    categories={brandThemingContext?.data.categories ?? []}
                    isLoading={brandThemingContext?.query.isLoading ?? false}
                  />
                </>
              )}
            </Content>
          </Root>
        </Portal>
      )}
    </TopRow>
  );
}

const TopRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  position: 'relative',
});

const SettingsButton = styled.span({
  cursor: 'pointer',
  padding: 6,
  borderRadius: 4,
  color: 'var(--gray-d)',
  backgroundColor: 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  '&:hover': {
    backgroundColor: '#f0f0f0',
    color: '#333',
  },
});

const StyledSettingsIcon = styled(Settings)({
  display: 'flex',
  flexShrink: 0,
});

const Root = styled.div(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  borderRadius: '5px',
  border: `1px solid ${theme.palette.gray.light1}`,
  boxShadow: '0 2px 6px 2px rgba(60, 64, 67, .15)',
  boxSizing: 'border-box',
  padding: '12px',
  minWidth: 280,
  zIndex: 1000,
}));

const Header = styled.div({
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '10px',
});

const HeaderLabel = styled.h1(({ theme }) => ({
  fontSize: '18px',
  fontFamily: theme.fonts.semiBold,
}));

const Content = styled.div({
  boxSizing: 'border-box',
  padding: '0 2px',
});

const LabelContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  marginBottom: '10px',
});

const Label = styled.label(({ theme }) => ({
  whiteSpace: 'nowrap',
  fontFamily: theme.fonts.semiBold,
  fontSize: '14px',
}));

const Divider = styled.div({
  height: '1px',
  backgroundColor: '#e0e0e0',
  margin: '16px 0',
});
